import React, { useState, useRef, useEffect } from 'react';
import { Send, Mic, MicOff, X, Bot, User, Sparkles, TrendingUp, Target, AlertTriangle } from 'lucide-react';

interface AIChatProps {
  isOpen: boolean;
  onClose: () => void;
  initialPrompt?: string;
}

interface Message {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
  category?: string;
  confidence?: number;
}

const AIChat: React.FC<AIChatProps> = ({ isOpen, onClose, initialPrompt }) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'ai',
      content: 'Hello! I\'m your ADAPT AI co-pilot. I can help you understand market signals, prioritise experiments, and guide strategic decisions. How can I assist you today?',
      timestamp: new Date(),
      category: 'greeting'
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle initial prompt from dynamic prompts
  useEffect(() => {
    if (initialPrompt && isOpen) {
      setInputMessage(initialPrompt);
      // Auto-send the prompt after a brief delay
      setTimeout(() => {
        handleSendMessage(initialPrompt);
      }, 500);
    }
  }, [initialPrompt, isOpen]);

  const getContextualResponse = (userInput: string): { content: string; category: string; confidence: number } => {
    const input = userInput.toLowerCase();
    
    // KPI Analysis responses
    if (input.includes('kpi') || input.includes('performance') || input.includes('96%') || input.includes('target')) {
      return {
        content: "📊 **KPI Performance Analysis (96% vs 90% target)**\n\n**Key Success Drivers:**\n• AI personalisation engine delivering 23% engagement boost\n• Mobile optimisation reducing friction by 32%\n• Customer retention strategies showing 15% improvement\n\n**Risk Factors to Monitor:**\n• Market saturation in key segments (35% risk)\n• Potential competitor response to pricing strategy\n• Resource allocation efficiency at current scale\n\n**Recommendations:**\n1. Scale AI personalisation to additional user segments\n2. Implement early warning system for market saturation\n3. Develop competitive moat through patent portfolio",
        category: 'analysis',
        confidence: 94
      };
    }
    
    // Experiment responses - Enhanced for re-prioritisation
    if (input.includes('experiment') || input.includes('hypothesis') || input.includes('personalisation') || input.includes('confidence') || input.includes('priorit') || input.includes('queue')) {
      // Trigger the reordering event
      const event = new CustomEvent('ai-chat-message', { 
        detail: { message: userInput } 
      });
      window.dispatchEvent(event);

      return {
        content: "🧪 **Experiment Queue Re-Prioritised**\n\n**New Priority Order (Impact × Confidence):**\n\n**#1 Mobile-First Checkout** (High Impact × 92% Confidence = 276)\n• Ready for full rollout - exceptional performance\n• Recommend immediate scaling to all users\n\n**#2 AI Personalisation** (High Impact × 78% Confidence = 234)\n• Strong performance, increase test population by 40%\n• Monitor engagement metrics closely\n\n**#3 Voice Interface Beta** (High Impact × 71% Confidence = 213)\n• Requires UX refinement before scaling\n• Focus on accessibility improvements\n\n**#4 Subscription Tier Pricing** (Medium Impact × 65% Confidence = 130)\n• Moderate priority, good for Q2 testing\n• Analyse pricing sensitivity data\n\n**#5 Social Media Integration** (Medium Impact × 45% Confidence = 90)\n• Lowest priority, consider pausing\n• Reallocate resources to higher-impact experiments\n\n**Next Actions:**\n1. Accelerate Mobile Checkout rollout timeline\n2. Expand AI Personalisation test scope\n3. Conduct Voice Interface usability testing\n4. Pause Social Media Integration development",
        category: 'experiments',
        confidence: 92
      };
    }
    
    // Specific Backlog Priority Optimisation response
    if (input.includes('update backlog priorities') || input.includes('backlog priorit')) {
      return {
        content: "📋 **Backlog Priority Optimisation**\n\n**Immediate Actions:**\n• Move 'Customer Feedback Analytics Dashboard' to HIGH priority (NPS improvements detected)\n• Expedite Security Audit (85% risk score requires attention)\n\n**Business Impact Forecast:**\n• Current changes could improve delivery efficiency by 22%\n• Risk mitigation value: £2.3M potential loss prevention\n\n---\n\n**Would you like me to apply these priority changes to your backlog?** I can automatically update the priority levels and notify the relevant team members.",
        category: 'backlog',
        confidence: 91
      };
    }

    // Specific Resource Allocation response
    if (input.includes('optimise resource allocation') || input.includes('resource allocation')) {
      return {
        content: "⚡ **Resource Reallocation Analysis**\n\n**Recommended Changes:**\n• **Sarah Chen**: Redistribute 3h from Voice Interface to AI Personalisation\n• **Mike Rodriguez**: Focus remaining 13h on Mobile Checkout completion\n• **David Kumar**: Prioritise Security Audit over feature development\n\n**Expected Outcomes:**\n• 22% improvement in delivery efficiency\n• Faster completion of high-impact initiatives\n• Better alignment with strategic priorities\n\n**Resource Utilisation:**\n• Current: 47 hours allocated\n• Optimised: Same hours, better distribution\n• Risk reduction: 15% through focused security work\n\n---\n\n**Would you like me to apply this resource reallocation?** I can update team assignments and send notifications to affected team members.",
        category: 'resource',
        confidence: 88
      };
    }

    // Specific Task Dependencies response
    if (input.includes('analyse task dependencies') || input.includes('task dependencies') || input.includes('dependency analysis')) {
      return {
        content: "🔗 **Task Dependency Analysis**\n\n**Critical Dependencies Identified:**\n\n**1. AI Personalisation Engine → Mobile Checkout**\n• Dependency: Personalisation APIs needed for checkout optimisation\n• Risk: 3-day delay if API development falls behind\n• Mitigation: Parallel development with mock APIs\n\n**2. Security Audit → All Production Releases**\n• Dependency: Security clearance required before any deployment\n• Risk: Blocks all releases if audit delayed\n• Mitigation: Prioritise audit completion, implement staged security reviews\n\n**3. Customer Feedback Dashboard → Voice Interface**\n• Dependency: User feedback data needed for voice UX decisions\n• Risk: Voice interface design may need rework\n• Mitigation: Use existing feedback data, implement feedback loop\n\n**4. Mobile Optimisation → Voice Interface**\n• Dependency: Mobile framework changes affect voice integration\n• Risk: Technical debt and integration complexity\n• Mitigation: Coordinate mobile and voice teams, shared architecture review\n\n**Proposed Mitigations:**\n1. **Create API mocks** for parallel development\n2. **Implement staged security reviews** to prevent bottlenecks\n3. **Establish weekly dependency sync meetings**\n4. **Develop shared technical architecture** for mobile/voice integration\n\n**Timeline Impact:**\n• Without mitigation: 2-3 week delays possible\n• With mitigation: Risk reduced to 3-5 days maximum",
        category: 'dependencies',
        confidence: 89
      };
    }
    
    // General backlog responses (fallback)
    if (input.includes('backlog') || input.includes('resource') || input.includes('allocation')) {
      return {
        content: "📋 **Backlog Management Overview**\n\n**Current Status:**\n• 5 active items in backlog\n• 3 high-priority items requiring immediate attention\n• Resource utilisation at 85% capacity\n\n**Key Areas:**\n• Priority optimisation needed for Customer Feedback Dashboard\n• Resource reallocation opportunities identified\n• Task dependencies require analysis\n\n**Next Steps:**\n1. Review priority assignments\n2. Optimise resource allocation\n3. Analyse task dependencies\n\nWhich specific area would you like me to focus on?",
        category: 'backlog',
        confidence: 85
      };
    }
    
    // Market insights and competitive analysis
    if (input.includes('market') || input.includes('competitive') || input.includes('threat') || input.includes('opportunity')) {
      return {
        content: "🎯 **Market Intelligence Summary**\n\n**Emerging Opportunities:**\n• AI personalisation market growing 45% YoY\n• Mobile-first demographics expanding in target segments\n• Voice commerce adoption accelerating (89% growth)\n\n**Competitive Threats:**\n• New market entrant with £50M funding targeting our segment\n• Price war initiated by major competitor (15% price reduction)\n• Innovation gap widening in voice interface technology\n\n**Strategic Recommendations:**\n1. Accelerate AI personalisation to maintain 6-month competitive moat\n2. Consider strategic partnerships for voice technology\n3. Enhance value proposition messaging to counter pricing pressure",
        category: 'market',
        confidence: 89
      };
    }
    
    // Risk mitigation responses
    if (input.includes('risk') || input.includes('mitigation') || input.includes('strategy')) {
      return {
        content: "⚠️ **Risk Mitigation Strategy**\n\n**Market Risk (35%) Mitigation:**\n• Diversify customer segments to reduce enterprise dependency\n• Accelerate international expansion (3 new markets)\n• Build recession-resistant product features\n\n**Competitive Risk (30%) Mitigation:**\n• Strengthen IP portfolio (file 12 additional patents)\n• Accelerate innovation cycles (reduce time-to-market by 25%)\n• Enhance customer lock-in through platform integrations\n\n**Implementation Timeline:**\n• Week 1-2: IP portfolio audit and filing strategy\n• Week 3-4: International market research initiation\n• Month 2: Customer diversification campaign launch",
        category: 'risk',
        confidence: 86
      };
    }
    
    // General strategic responses
    return {
      content: "🚀 **Strategic Insight Analysis**\n\n**Key Correlation Identified:**\nAI personalisation adoption shows strong correlation with customer lifetime value (+34%). This represents our strongest competitive advantage.\n\n**Strategic Recommendations:**\n1. **Double down on ML capabilities** whilst competitors are catching up\n2. **Accelerate mobile-native features** as differentiators\n3. **Expand data science team** to maintain innovation velocity\n\n**Market Positioning:**\n• Current AI personalisation advantage provides 6-month competitive moat\n• Mobile-first trend accelerating faster than industry average\n• Customer satisfaction improvements creating organic growth opportunities\n\n**Next Steps:**\n• Schedule executive review of AI investment strategy\n• Evaluate acquisition targets for mobile technology\n• Develop customer success metrics dashboard",
      category: 'strategy',
      confidence: 92
    };
  };

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || inputMessage;
    if (!textToSend.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: textToSend,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    // Simulate AI processing time
    setTimeout(() => {
      const response = getContextualResponse(textToSend);
      
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: response.content,
        timestamp: new Date(),
        category: response.category,
        confidence: response.confidence
      };

      setMessages(prev => [...prev, aiMessage]);
      setIsTyping(false);
    }, 1500 + Math.random() * 1000); // Variable response time for realism
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleVoice = () => {
    setIsListening(!isListening);
    // Voice recognition would be implemented here
    if (!isListening) {
      // Simulate voice input
      setTimeout(() => {
        setInputMessage("Analyse current KPI performance and suggest improvements");
        setIsListening(false);
      }, 3000);
    }
  };

  const getCategoryIcon = (category?: string) => {
    switch (category) {
      case 'analysis': return <TrendingUp className="w-4 h-4 text-blue-400" />;
      case 'experiments': return <Sparkles className="w-4 h-4 text-purple-400" />;
      case 'backlog': return <Target className="w-4 h-4 text-green-400" />;
      case 'resource': return <Target className="w-4 h-4 text-cyan-400" />;
      case 'dependencies': return <Target className="w-4 h-4 text-pink-400" />;
      case 'market': return <TrendingUp className="w-4 h-4 text-orange-400" />;
      case 'risk': return <AlertTriangle className="w-4 h-4 text-red-400" />;
      case 'strategy': return <Target className="w-4 h-4 text-indigo-400" />;
      default: return <Bot className="w-4 h-4 text-indigo-400" />;
    }
  };

  const getCategoryColor = (category?: string) => {
    switch (category) {
      case 'analysis': return 'border-l-blue-400';
      case 'experiments': return 'border-l-purple-400';
      case 'backlog': return 'border-l-green-400';
      case 'resource': return 'border-l-cyan-400';
      case 'dependencies': return 'border-l-pink-400';
      case 'market': return 'border-l-orange-400';
      case 'risk': return 'border-l-red-400';
      case 'strategy': return 'border-l-indigo-400';
      default: return 'border-l-gray-400';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-6 right-6 w-[90vw] max-w-[480px] h-[80vh] max-h-[600px] bg-gray-800 rounded-xl shadow-2xl z-50 flex flex-col border border-gray-700 lg:bottom-24">
      {/* Header */}
      <div className="flex items-center justify-between p-3 lg:p-4 border-b border-gray-700 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-xl">
        <div className="flex items-center space-x-2 lg:space-x-3">
          <div className="w-6 h-6 lg:w-8 lg:h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <Bot className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm lg:text-base">ADAPT AI Co-Pilot</h3>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-xs text-gray-200">Online & Ready</span>
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-200 hover:text-white transition-colours p-1 rounded-lg hover:bg-white hover:bg-opacity-20"
        >
          <X size={18} className="lg:w-5 lg:h-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 lg:p-4 space-y-3 lg:space-y-4 bg-gray-900">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] px-3 lg:px-4 py-2 lg:py-3 rounded-xl ${
                message.type === 'user'
                  ? 'bg-indigo-600 text-white rounded-br-sm'
                  : `bg-gray-800 text-gray-100 rounded-bl-sm border-l-4 ${getCategoryColor(message.category)}`
              }`}
            >
              <div className="flex items-start space-x-2 lg:space-x-3">
                {message.type === 'ai' && (
                  <div className="mt-1 flex-shrink-0">
                    {getCategoryIcon(message.category)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs lg:text-sm leading-relaxed whitespace-pre-line break-words">
                    {message.content}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs opacity-70">
                      {message.timestamp.toLocaleTimeString()}
                    </span>
                    {message.confidence && (
                      <div className="flex items-center space-x-1">
                        <span className="text-xs opacity-70">Confidence:</span>
                        <span className={`text-xs font-medium ${
                          message.confidence >= 90 ? 'text-green-400' :
                          message.confidence >= 80 ? 'text-yellow-400' : 'text-orange-400'
                        }`}>
                          {message.confidence}%
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-gray-800 text-gray-100 max-w-[85%] px-3 lg:px-4 py-2 lg:py-3 rounded-xl rounded-bl-sm border-l-4 border-l-indigo-400">
              <div className="flex items-center space-x-2 lg:space-x-3">
                <Bot size={14} className="lg:w-4 lg:h-4 text-indigo-400 flex-shrink-0" />
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
                <span className="text-xs text-gray-400">Analysing...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 lg:p-4 border-t border-gray-700 bg-gray-800 rounded-b-xl">
        <div className="flex items-end space-x-2 lg:space-x-3">
          <div className="flex-1 relative">
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything..."
              className="w-full px-3 lg:px-4 py-2 lg:py-3 bg-gray-700 text-white rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 border border-gray-600 min-h-[44px] max-h-32 text-sm lg:text-base"
              rows={1}
              style={{ 
                height: 'auto',
                minHeight: '44px'
              }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = Math.min(target.scrollHeight, 128) + 'px';
              }}
            />
          </div>
          <button
            onClick={toggleVoice}
            className={`p-2 lg:p-3 rounded-xl transition-all duration-200 flex-shrink-0 ${
              isListening 
                ? 'bg-red-600 text-white shadow-lg animate-pulse' 
                : 'bg-gray-700 text-gray-300 hover:text-white hover:bg-gray-600'
            }`}
          >
            {isListening ? <MicOff size={18} className="lg:w-5 lg:h-5" /> : <Mic size={18} className="lg:w-5 lg:h-5" />}
          </button>
          <button
            onClick={() => handleSendMessage()}
            disabled={!inputMessage.trim() || isTyping}
            className="p-2 lg:p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-lg transform hover:scale-105 disabled:transform-none flex-shrink-0"
          >
            <Send size={18} className="lg:w-5 lg:h-5" />
          </button>
        </div>
        
        {/* Quick suggestion chips */}
        <div className="flex flex-wrap gap-2 mt-3">
          {[
            "Analyse KPI trends",
            "Prioritise experiments",
            "Risk assessment",
            "Market opportunities"
          ].map((suggestion, index) => (
            <button
              key={index}
              onClick={() => setInputMessage(suggestion)}
              className="px-2 lg:px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-xs hover:bg-gray-600 hover:text-white transition-colours"
            >
              {suggestion}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AIChat;